import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { api } from '../services/api';

/**
 * Growth this month — the owner, Sep 19 2026: "add new customers this month
 * as a dashboard metric, and CPC, and close rate etc."
 *
 * New customers · leads · close rate · marketing spend · cost per customer
 * (and per lead), each beside last month. The spend is what he types for the
 * month, else what the bank feed sorted into Marketing; with no spend or
 * nobody new there is a dash, never a guess. Backed by GET /dashboard/growth.
 */
const money = (n: number | null | undefined) => n === null || n === undefined ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
const monthName = (key?: string) => key ? new Date(`${key}-15T12:00:00`).toLocaleDateString('en-US', { month: 'long' }) : '';

function Delta({ now, before, goodWhenUp = true }: { now: number | null; before: number | null; goodWhenUp?: boolean }) {
  if (now === null || before === null || now === before) return null;
  const up = now > before;
  const good = up === goodWhenUp;
  const Icon = up ? TrendingUp : TrendingDown;
  return <Icon className={`inline h-3.5 w-3.5 ml-1 ${good ? 'text-emerald-600' : 'text-red-500'}`} />;
}

function Tile({ label, value, sub, children }: { label: string; value: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5 tabular-nums">{value}</p>
      {sub && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{sub}</p>}
      {children}
    </div>
  );
}

export default function GrowthCard() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery('dashboard-growth', () => api.get('/dashboard/growth').then(r => r.data), { staleTime: 60_000 });
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const save = useMutation((value: string | null) => api.put('/dashboard/growth/spend', { month: data?.month, amount: value }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message || 'Saved'); setEditing(false); qc.invalidateQueries('dashboard-growth'); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  if (isLoading) return <div className="card p-4 text-sm text-gray-500">Figuring this month…</div>;
  if (isError || !data) return null;
  const t = data.this, l = data.last, lastName = monthName(data.last_month);
  const was = (v: any, fmt: (x: any) => string = String) => `${lastName}: ${v === null || v === undefined ? '—' : fmt(v)}`;
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h2 className="text-sm font-black uppercase tracking-tight text-gray-900">Growth — {monthName(data.month)}</h2>
        <Link to="/leads" className="text-xs font-semibold text-brand-700">Leads</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Tile label="New customers" value={<>{t.new_customers}<Delta now={t.new_customers} before={l.new_customers} /></>} sub={was(l.new_customers)} />
        <Tile label="New leads" value={<>{t.leads}<Delta now={t.leads} before={l.leads} /></>} sub={was(l.leads)} />
        <Tile label="Close rate" value={<>{t.close_rate === null ? '—' : `${t.close_rate}%`}<Delta now={t.close_rate} before={l.close_rate} /></>}
          sub={<>{t.won} won · {t.lost} lost · {t.open} still out{data.ninety_days?.close_rate !== null && data.ninety_days?.close_rate !== undefined ? <> · 90 days: {data.ninety_days.close_rate}%</> : null}</>} />
        <Tile label="Marketing spend" value={money(t.spend_source === 'none' ? null : t.spend)}
          sub={t.spend_source === 'typed' ? 'typed by you' : t.spend_source === 'bank' ? 'from the bank, sorted as Marketing' : 'nothing recorded this month'}>
          {editing ? (
            <form className="mt-1.5 flex gap-1" onSubmit={e => { e.preventDefault(); save.mutate(amount.trim() === '' ? null : amount.trim()); }}>
              <input autoFocus inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="$ this month" className="input text-xs py-1 px-2 w-24" />
              <button type="submit" disabled={save.isLoading} className="press text-xs font-semibold text-black bg-brand-600 rounded-xl px-2 disabled:opacity-50">Save</button>
              <button type="button" onClick={() => setEditing(false)} className="text-xs text-gray-500 px-1">Cancel</button>
            </form>
          ) : (
            <button onClick={() => { setAmount(t.spend_source === 'typed' ? String(t.spend) : ''); setEditing(true); }} className="mt-1 text-[11px] font-semibold text-brand-700">{t.spend_source === 'typed' ? 'Change' : 'Type what you spent'}</button>
          )}
        </Tile>
        <Tile label="Cost per customer" value={<>{money(t.cost_per_customer)}<Delta now={t.cost_per_customer} before={l.cost_per_customer} goodWhenUp={false} /></>}
          sub={<>per lead: {money(t.cost_per_lead)} · {was(l.cost_per_customer, money)}</>} />
      </div>
      <p className="text-[11px] text-gray-500">A new customer is someone whose first real job went on the books this month; yards brought in by an import don't count. Cost per customer is this month's marketing spend divided by them.</p>
    </section>
  );
}
